import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ContactForms = () => {
  console.log("ContactForms component loaded with edge functions!");

  const { toast } = useToast();
  const [counts, setCounts] = useState({ buyersCount: 0, investorsCount: 0 });

  const [buyerForm, setBuyerForm] = useState({
    name: "",
    email: "",
    location: "",
    user_type: "first-time-buyer",
  });

  const [investorForm, setInvestorForm] = useState({
    name: "",
    email: "",
    organization: "",
    user_type: "individual-investor",
  });

  const [buyerLoading, setBuyerLoading] = useState(false);
  const [investorLoading, setInvestorLoading] = useState(false);

  const [buyerStatus, setBuyerStatus] = useState<string | null>(null);
  const [investorStatus, setInvestorStatus] = useState<string | null>(null);

  const debugMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debug") === "1";

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get_counts");
        if (error) throw error;
        setCounts(data);
        console.log("📊 Initial counts loaded:", data);
      } catch (error) {
        console.error("❌ Error loading counts:", error);
      }
    };
    loadCounts();
  }, []);

  const handleBuyerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buyerForm.name || !buyerForm.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in your name and email address.",
        variant: "destructive",
      });
      return;
    }

    setBuyerLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("submit_form", {
        body: { formType: "buyer", formData: buyerForm },
      });
      if (error) throw error;
      if (!data.ok) throw new Error(data.error);

      setBuyerStatus(`Success (Edge Function). ID: ${data.data.id}`);
      toast({
        title: "Successfully joined waitlist!",
        description: "We'll notify you when we launch in your area.",
      });

      setBuyerForm({
        name: "",
        email: "",
        location: "",
        user_type: "first-time-buyer",
      });

      const countsResult = await supabase.functions.invoke("get_counts");
      if (countsResult.data) setCounts(countsResult.data);
    } catch (error: any) {
      console.error("❌ Error in buyer form submission:", error);
      toast({
        title: "Submission issue",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      setBuyerStatus("Failed: edge function error");
    } finally {
      setBuyerLoading(false);
    }
  };

  const handleInvestorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!investorForm.name || !investorForm.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in your name and email address.",
        variant: "destructive",
      });
      return;
    }

    setInvestorLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("submit_form", {
        body: { formType: "investor", formData: investorForm },
      });
      if (error) throw error;
      if (!data.ok) throw new Error(data.error);

      setInvestorStatus(`Success (Edge Function). ID: ${data.data.id}`);
      toast({
        title: "Request submitted successfully!",
        description: "We'll send you the investor brief shortly.",
      });

      setInvestorForm({
        name: "",
        email: "",
        organization: "",
        user_type: "individual-investor",
      });

      const countsResult = await supabase.functions.invoke("get_counts");
      if (countsResult.data) setCounts(countsResult.data);
    } catch (error: any) {
      console.error("❌ Error in investor form submission:", error);
      toast({
        title: "Submission issue",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
      setInvestorStatus("Failed: edge function error");
    } finally {
      setInvestorLoading(false);
    }
  };

  return (
    <section id="cta" className="py-16">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground mb-4">Get in Touch</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Two simple paths — one for home buyers interested in buying without the
            20% down payment, and one for investors exploring a new asset class.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Home Buyers Form */}
          <Card
            id="waitlist"
            className="p-8 bg-buyer-card border-border flex flex-col h-full min-h-96"
          >
            {/* Make the inner wrapper a flex column so the form can stretch */}
            <div className="flex flex-col gap-6 flex-1">
              <div className="flex items-center gap-4">
                <Badge variant="secondary">Home Buyers</Badge>
                <div className="text-2xl">📝</div>
              </div>

              <h3 className="text-xl font-semibold text-foreground">
                Join the Waitlist
              </h3>

              <p className="text-muted-foreground">
                Introduce yourself — SmartEQT™ helps you buy without the traditional
                20% down payment and no additional debt. We'll follow up with the right
                level of detail.
              </p>

              {/* Form stretches inside the flex wrapper */}
              <form
                onSubmit={handleBuyerSubmit}
                className="flex flex-col flex-1 gap-4"
              >
                <div>
                  <Label htmlFor="buyer-name">Name *</Label>
                  <Input
                    id="buyer-name"
                    required
                    value={buyerForm.name}
                    onChange={(e) =>
                      setBuyerForm({ ...buyerForm, name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="buyer-email">Email *</Label>
                  <Input
                    id="buyer-email"
                    type="email"
                    required
                    value={buyerForm.email}
                    onChange={(e) =>
                      setBuyerForm({ ...buyerForm, email: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="buyer-location">Location (City/State)</Label>
                  <Input
                    id="buyer-location"
                    value={buyerForm.location}
                    onChange={(e) =>
                      setBuyerForm({ ...buyerForm, location: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="buyer-type">I am</Label>
                  <Select
                    value={buyerForm.user_type}
                    onValueChange={(value) =>
                      setBuyerForm({ ...buyerForm, user_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first-time-buyer">
                        First-time Home Buyer
                      </SelectItem>
                      <SelectItem value="repeat-buyer">
                        Repeat Home Buyer
                      </SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Action area pinned to bottom */}
                <div className="mt-auto space-y-2">
                  <Button
                    type="submit"
                    disabled={buyerLoading}
                    className="w-full bg-accent hover:bg-accent-hover text-accent-foreground"
                  >
                    {buyerLoading ? "Joining..." : "Join Waitlist"}
                  </Button>
                  {debugMode && buyerStatus && (
                    <p className="text-xs text-muted-foreground">{buyerStatus}</p>
                  )}
                </div>
              </form>
            </div>
          </Card>

          {/* Investors Form */}
          <Card
            id="investors"
            className="p-8 bg-investor-card border-border flex flex-col h-full min-h-96"
          >
            {/* Make the inner wrapper a flex column so the form can stretch */}
            <div className="flex flex-col gap-6 flex-1">
              <div className="flex items-center gap-4">
                <Badge variant="secondary">Investors</Badge>
                <div className="text-2xl">📊</div>
              </div>

              <h3 className="text-xl font-semibold text-foreground">
                Request Investor Brief
              </h3>

              <p className="text-muted-foreground">
                Tell us who you are — SmartEQT™ unlocks a new path for buyers while
                creating a new asset class for investors. We'll share materials as
                appropriate.
              </p>

              {/* Form stretches inside the flex wrapper */}
              <form
                onSubmit={handleInvestorSubmit}
                className="flex flex-col flex-1 gap-4"
              >
                <div>
                  <Label htmlFor="investor-name">Name *</Label>
                  <Input
                    id="investor-name"
                    required
                    value={investorForm.name}
                    onChange={(e) =>
                      setInvestorForm({ ...investorForm, name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="investor-email">Email *</Label>
                  <Input
                    id="investor-email"
                    type="email"
                    required
                    value={investorForm.email}
                    onChange={(e) =>
                      setInvestorForm({ ...investorForm, email: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="investor-organization">Organization</Label>
                  <Input
                    id="investor-organization"
                    value={investorForm.organization}
                    onChange={(e) =>
                      setInvestorForm({
                        ...investorForm,
                        organization: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="investor-type">I am</Label>
                  <Select
                    value={investorForm.user_type}
                    onValueChange={(value) =>
                      setInvestorForm({ ...investorForm, user_type: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual-investor">
                        Individual Investor
                      </SelectItem>
                      <SelectItem value="institutional-investor">
                        Institutional Investor
                      </SelectItem>
                      <SelectItem value="vc">VC</SelectItem>
                      <SelectItem value="pe">PE</SelectItem>
                      <SelectItem value="family-office">Family Office</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Action area pinned to bottom */}
                <div className="mt-auto space-y-2">
                  <Button
                    type="submit"
                    disabled={investorLoading}
                    className="w-full bg-investor hover:bg-investor-hover text-investor-foreground"
                  >
                    {investorLoading ? "Submitting..." : "Request Brief"}
                  </Button>
                  {debugMode && investorStatus && (
                    <p className="text-xs text-muted-foreground">{investorStatus}</p>
                  )}
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactForms;
